import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import LinearProgress from '@material-ui/core/LinearProgress'
import MenuItem from '@material-ui/core/MenuItem'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Autocomplete from '@material-ui/lab/Autocomplete'
import AutocompleteMultiselect from 'components/autocomplete-multiselect'
import { useDebounce } from 'lib/hooks'
import { useCoachStudentSearch } from '../api-hooks'
import {
  AGE_GROUP_OPTIONS,
  NAME_MAX_LENGTH,
  RECENCY_OPTIONS,
  SKILL_LEVEL_OPTIONS,
} from '../constants'
import { applyRecency, pluralize, recencyValue, studentName } from '../format'
import useStyles from '../styles'
import SectionTitle from 'components/section-title'

const optionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
})

const IdFilter = ({ label, options, ids, onChange }) => {
  if (options.length === 0) return null

  return (
    <AutocompleteMultiselect
      options={options}
      value={options.filter(option => ids.includes(option.id))}
      getOptionLabel={option => option.label}
      getOptionSelected={(option, value) => option.id === value.id}
      onChange={(_event, selected) => onChange(selected.map(o => o.id))}
      renderInput={params => (
        <TextField {...params} variant="outlined" label={label} />
      )}
    />
  )
}

IdFilter.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionShape).isRequired,
  ids: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
}

const StudentPicker = ({ pickedStudents, onChange, skip }) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { data, loading } = useCoachStudentSearch(debouncedSearch, {
    skip: skip || debouncedSearch.trim().length < 2,
  })

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      options={data?.coachStudents || []}
      value={pickedStudents}
      loading={loading}
      getOptionLabel={studentName}
      getOptionSelected={(option, value) => option.id === value.id}
      filterOptions={options => options}
      onInputChange={(_event, value) => setSearch(value)}
      onChange={(_event, students) => onChange(students)}
      noOptionsText={
        debouncedSearch.trim().length < 2
          ? 'Type a name to search'
          : 'No students found'
      }
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          label="Students"
          placeholder="Search your students"
        />
      )}
    />
  )
}

StudentPicker.propTypes = {
  pickedStudents: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  skip: PropTypes.bool,
}

export default function AudienceStep({
  campaign,
  options,
  audience,
  audienceLoading,
  mode,
  pickedStudents,
  nameError,
  skipSearch,
  onChange,
  onModeChange,
  onPickStudents,
}) {
  const classes = useStyles()
  const filters = campaign.audienceFilters

  const setFilters = patch =>
    onChange({ audienceFilters: { ...filters, ...patch } })

  const toggleSkill = value =>
    setFilters({
      skillLevels: filters.skillLevels.includes(value)
        ? filters.skillLevels.filter(level => level !== value)
        : [...filters.skillLevels, value],
    })

  const toggleExcluded = studentId =>
    setFilters({
      excludedStudentIds: filters.excludedStudentIds.includes(studentId)
        ? filters.excludedStudentIds.filter(id => id !== studentId)
        : [...filters.excludedStudentIds, studentId],
    })

  const excludedCount = filters.excludedStudentIds.length
  const count = audience?.count ?? 0
  const maxRecipients = audience?.maxRecipients

  return (
    <>
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>Campaign name</SectionTitle>
        </Box>
        <Box className={classes.cardBody}>
          <TextField
            id="campaign-name"
            variant="outlined"
            label="Name"
            value={campaign.name}
            onChange={event => onChange({ name: event.target.value })}
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
            error={nameError}
            helperText={
              nameError
                ? 'Give your campaign a name'
                : 'Only you see this. For example, "Spring clinic openings".'
            }
            fullWidth
          />
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>Who gets it</SectionTitle>
          <Typography className={classes.cardSubtitle}>
            Parents or guardians get the message for juniors.
          </Typography>
        </Box>
        <Box className={classes.cardBody}>
          <RadioGroup
            row
            value={mode}
            onChange={event => onModeChange(event.target.value)}
            aria-label="Choose students"
          >
            <FormControlLabel
              value="filters"
              control={<Radio color="primary" />}
              label="Students who match filters"
            />
            <FormControlLabel
              value="pick"
              control={<Radio color="primary" />}
              label="Students I pick"
            />
          </RadioGroup>

          {mode === 'pick' ? (
            <StudentPicker
              pickedStudents={pickedStudents}
              skip={skipSearch}
              onChange={onPickStudents}
            />
          ) : (
            <>
              <Box display="flex" gridGap={16} flexWrap="wrap">
                <TextField
                  id="campaign-age-group"
                  select
                  variant="outlined"
                  label="Age"
                  value={filters.ageGroup}
                  onChange={event =>
                    setFilters({ ageGroup: event.target.value })
                  }
                  style={{ minWidth: 180 }}
                >
                  {AGE_GROUP_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  id="campaign-lesson-recency"
                  select
                  variant="outlined"
                  label="Lessons with you"
                  value={recencyValue(filters)}
                  onChange={event =>
                    onChange({
                      audienceFilters: applyRecency(
                        filters,
                        event.target.value,
                      ),
                    })
                  }
                  style={{ minWidth: 280, flex: 1 }}
                >
                  {RECENCY_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <Typography className={classes.fieldGroupLabel}>
                  Golf experience
                </Typography>
                <Box className={classes.chips}>
                  {SKILL_LEVEL_OPTIONS.map(option => {
                    const selected = filters.skillLevels.includes(option.value)
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'default' : 'outlined'}
                        onClick={() => toggleSkill(option.value)}
                        aria-pressed={selected}
                      />
                    )
                  })}
                </Box>
                <Typography
                  className={`${classes.helperText} ${classes.chipsHelper}`}
                >
                  From students’ golfer profiles. Students who haven’t filled
                  one out are left out when you pick a level.
                </Typography>
              </Box>

              <IdFilter
                label="Booked these booking types"
                options={options.lessonTypes}
                ids={filters.lessonTypeIds}
                onChange={lessonTypeIds => setFilters({ lessonTypeIds })}
              />
              <IdFilter
                label="Came to these group events"
                options={options.groupEvents}
                ids={filters.groupEventIds}
                onChange={groupEventIds => setFilters({ groupEventIds })}
              />
              <IdFilter
                label="In these programs"
                options={options.programs}
                ids={filters.programIds}
                onChange={programIds => setFilters({ programIds })}
              />
              <IdFilter
                label="Had lessons at these locations"
                options={options.facilities}
                ids={filters.facilityIds}
                onChange={facilityIds => setFilters({ facilityIds })}
              />
            </>
          )}
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.audienceSummary}>
            <Typography
              className={classes.audienceCount}
              data-testid="audience-count"
            >
              {count}
            </Typography>
            <Typography className={classes.audienceCountLabel}>
              {count === 1 ? 'student' : 'students'}
            </Typography>
          </Box>
          <Typography className={classes.cardSubtitle}>
            {maxRecipients && count > maxRecipients
              ? `Campaigns can go to up to ${maxRecipients.toLocaleString()} students. Narrow your filters to continue.`
              : 'Uncheck anyone you want to leave out.'}
          </Typography>
        </Box>
        {audienceLoading ? <LinearProgress color="primary" /> : null}
        <Box className={classes.cardBody}>
          {audience?.students?.length ? (
            <Box className={classes.previewList}>
              {audience.students.map(student => {
                const excluded = filters.excludedStudentIds.includes(student.id)
                return (
                  <Box key={student.id} className={classes.previewRow}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={!excluded}
                          onChange={() => toggleExcluded(student.id)}
                        />
                      }
                      label={
                        <Typography
                          className={
                            excluded
                              ? `${classes.previewName} ${classes.excluded}`
                              : classes.previewName
                          }
                        >
                          {studentName(student)}
                          {student.isMinor ? ' (junior)' : ''}
                        </Typography>
                      }
                    />
                  </Box>
                )
              })}
            </Box>
          ) : (
            <Typography className={classes.helperText}>
              {audienceLoading
                ? 'Finding students…'
                : 'No students match. Try fewer filters.'}
            </Typography>
          )}
          {count > (audience?.students?.length || 0) ? (
            <Typography className={classes.helperText}>
              Showing the first {audience.students.length} by name.
            </Typography>
          ) : null}
          {excludedCount > 0 ? (
            <Box display="flex" alignItems="center" gridGap={8}>
              <Typography className={classes.helperText}>
                {pluralize(excludedCount, 'student')} left out
              </Typography>
              <Button
                size="small"
                color="primary"
                onClick={() => setFilters({ excludedStudentIds: [] })}
              >
                Add Back
              </Button>
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  )
}

AudienceStep.propTypes = {
  campaign: PropTypes.shape({
    name: PropTypes.string.isRequired,
    audienceFilters: PropTypes.object.isRequired,
  }).isRequired,
  options: PropTypes.shape({
    lessonTypes: PropTypes.arrayOf(optionShape).isRequired,
    groupEvents: PropTypes.arrayOf(optionShape).isRequired,
    programs: PropTypes.arrayOf(optionShape).isRequired,
    facilities: PropTypes.arrayOf(optionShape).isRequired,
  }).isRequired,
  audience: PropTypes.shape({
    count: PropTypes.number.isRequired,
    maxRecipients: PropTypes.number.isRequired,
    students: PropTypes.array.isRequired,
  }),
  audienceLoading: PropTypes.bool,
  mode: PropTypes.oneOf(['filters', 'pick']).isRequired,
  pickedStudents: PropTypes.array.isRequired,
  nameError: PropTypes.bool,
  skipSearch: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onPickStudents: PropTypes.func.isRequired,
}
